import { getAuth0Logout, setTokenGetter } from './token-holder';
import { clearScreenLock } from './screen-lock';

export type LogoutReason =
  | 'user-initiated'
  | 'token-rejected'
  | 'ws-token-rejected'
  | 'sse-token-rejected'
  | 'idle-timeout'
  | 'wrong-tenant';

interface ForceLogoutOpts {
  reason: LogoutReason;
}

// ── Tenant mismatch: a TERMINAL auth state, not an expired session ──────────────────────
//
// operator-bff rejects an operator whose airline is not the one this console serves
// (`operator-bff:tenant-mismatch-401@v1`, docs/tenant-isolation-model.md §4.4). It is a 401,
// but it must NOT take the ordinary 401 path: `forceLogout` redirects to /login, Auth0 still
// holds a perfectly valid session for the operator's OWN airline, silent auth mints another
// token, the next request 401s identically — a redirect loop that never explains itself.
//
// So the mismatch latches here instead. The session is torn down (socket closed, token
// getter dropped) WITHOUT a redirect, and `TenantGuard` renders one terminal screen with an
// explicit sign-out. Signing out is the operator's decision, taken once, not a loop.

/** Which control rejected the token. `org_pin` fires before claims are decoded. */
export type TenantMismatchReason = 'org_pin' | 'airline_claim';

/** The operator-bff:tenant-mismatch-401@v1 body, normalised for the UI. */
export interface ITenantMismatch {
  reason: TenantMismatchReason;
  /** The airline this console serves. Null when the deployment could not name it. */
  expectedAirlineUrn: string | null;
  /** The airline the token claimed. ALWAYS null when `reason` is `org_pin`. */
  tokenAirlineUrn: string | null;
  /** Server-supplied copy. Null falls back to the UI's own wording. */
  message: string | null;
}

export interface ITenantMismatchState {
  mismatch: ITenantMismatch | null;
  /** Teardown in flight — the socket is still closing. */
  signingOut: boolean;
}

const NO_MISMATCH: ITenantMismatchState = { mismatch: null, signingOut: false };

// One immutable object per transition, so `useSyncExternalStore` sees a stable identity
// between changes and does not re-render on every read.
let mismatchState: ITenantMismatchState = NO_MISMATCH;
const mismatchListeners = new Set<() => void>();

function setMismatchState(next: ITenantMismatchState): void {
  mismatchState = next;
  for (const listener of mismatchListeners) listener();
}

export function getTenantMismatchState(): ITenantMismatchState {
  return mismatchState;
}

export function subscribeTenantMismatch(listener: () => void): () => void {
  mismatchListeners.add(listener);
  return () => {
    mismatchListeners.delete(listener);
  };
}

/**
 * Latch the terminal tenant-mismatch state and tear the session down WITHOUT redirecting.
 *
 * **Idempotent by contract.** Every in-flight request fails the same way, and react-query
 * may retry on top of that, so this is called many times for one rejection. Only the first
 * call latches and tears down; the rest return immediately. That is what makes "exactly one
 * rejection render, no repeated token exchange" true rather than aspirational.
 */
export async function enterTenantMismatch(mismatch: ITenantMismatch): Promise<void> {
  if (mismatchState.mismatch) return;

  setMismatchState({ mismatch, signingOut: true });

  // Same teardown as a real logout, minus step 2's redirect: close the socket so the BFF
  // releases this session's locks (an operator must not hold locks — or sit on a live feed —
  // for an airline they cannot read), then drop the token getter so nothing re-authenticates
  // behind the terminal screen.
  await closeWebSocket();
  setTokenGetter(null);

  setMismatchState({ mismatch, signingOut: false });
}

/** Test seam — module state would otherwise leak the latch across cases. */
export function resetTenantMismatchForTests(): void {
  mismatchState = NO_MISMATCH;
  mismatchListeners.clear();
}

// Operator-specific WS-close hook. NEX-147 (Socket.IO singleton client) registers
// its disconnect handler here so step 1 of the logout sequence actually releases
// locks. Until NEX-147 ships, the closer is null and step 1 is a no-op — matches
// the operator-ui-tds.md §7 contract: "Logout sequence per NEX-96: close
// Socket.IO first … this step is non-negotiable".

type WsCloser = () => Promise<void>;
let wsCloser: WsCloser | null = null;

export function registerWebSocketCloser(fn: WsCloser | null): void {
  wsCloser = fn;
}

/**
 * Step 1 of the logout sequence, shared by `forceLogout` and `enterTenantMismatch`.
 * Best-effort with a ~200 ms cap: a socket that will not close must never be the reason an
 * operator is stuck on a screen they cannot act on.
 */
async function closeWebSocket(): Promise<void> {
  if (!wsCloser) return;
  try {
    await Promise.race([
      wsCloser(),
      new Promise<void>((resolve) => setTimeout(resolve, 200)),
    ]);
  } catch (err) {
    console.warn('[force-logout] WebSocket close failed; proceeding.', err);
  }
}

/**
 * Three-step logout sequence per docs/ui/operator-ui-tds.md §7 + auth-tds.md §9.
 *
 *   1. **Close Socket.IO first** — releases held locks via OperatorDisconnected
 *      (NEX-71). This step is load-bearing for the operator console: skipping it
 *      means an operator walks away with held locks for ~30 s of TTL while their
 *      replacement is locked out. Best-effort, ~200 ms timeout.
 *   2. **Revoke refresh token + clear Auth0 SDK state** via SDK logout(), which
 *      triggers a redirect to /login?reason=...
 *   3. **Best-effort clear app-local state** — today the persisted screen lock
 *      (`screen-lock.ts`); future Zustand persist stores hook in here.
 *
 * Step 1 MUST complete before step 2 — the SDK's logout() redirects, tearing
 * down any in-flight WebSocket close that hasn't ACKd.
 */
export async function forceLogout({ reason }: ForceLogoutOpts): Promise<void> {
  // A latched tenant mismatch outranks a machine-triggered logout. The socket close that
  // teardown performs makes `socket.ts` report 4401 → 'ws-token-rejected', and the idle
  // monitor keeps ticking behind the terminal screen; either would redirect to /login and
  // restart the very loop the latch exists to stop. Human-initiated sign-out still gets
  // through — that is the affordance the screen offers, and the whole point of showing it.
  if (mismatchState.mismatch && reason !== 'user-initiated' && reason !== 'wrong-tenant') {
    return;
  }

  // Step 1 — close Socket.IO so the BFF releases locks owned by this session.
  await closeWebSocket();

  // Step 2 — revoke + clear SDK state.
  const auth0Logout = getAuth0Logout();
  setTokenGetter(null);
  if (auth0Logout) {
    await auth0Logout({
      logoutParams: { returnTo: `${window.location.origin}/login?reason=${reason}` },
    });
  } else {
    // Bridge wasn't mounted — fall back to a hard navigation so at least
    // in-memory state goes away. This path indicates a setup bug.
    console.warn('[force-logout] Auth0 logout function not registered; hard-redirecting.');
    window.location.href = `/login?reason=${reason}`;
  }

  // Step 3 — app-local state. A persisted screen lock must not greet the next sign-in.
  clearScreenLock();
}
